import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import Checkbox from "@/components/checkbox/Checkbox";
import CredentialsHeader from "@/components/header/Header";
import SimpleErrorMessage from "@/components/error-message/new-error-message";

import alertCircle from "@/assets/images/alert-circle.svg";

import { getFreshStartOriginConfig } from "@/config/FreshStartOriginConfig";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import { FormError } from "@/models/continente-credentials/common/FormError";
import { FreshStartResponse } from "@/models/shared/freshStart/FreshStartModels";

import { tryCatch } from "@/utils/try-catch";
import { genericService } from "@/services/GenericService";

const CONSEQUENCE_KEYS = [
    "phonenumber_login_fresh_start_release.consequence_account",
    "phonenumber_login_fresh_start_release.consequence_card",
    "phonenumber_login_fresh_start_release.consequence_new_card",
];
type Form = FormError;

export default function FreshStartRelease() {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation<any>();
    const { handleResponse } = useApiResponseHandler();

    const [loading, setLoading] = useState(false);
    const [understood, setUnderstood] = useState(false);

    const nextStep = location.state?.nextStep || "";

    const formState = useForm<Form>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
    });

    const {
        setError,
        clearErrors,
        formState: { errors },
    } = formState;

    const properties = location.state?.properties || {};
    const { backXTimes } = getFreshStartOriginConfig(properties.freshStartOrigin);

    const cancel = () => history.go(-backXTimes);

    const release = async () => {
        if (!understood) return;

        clearErrors();
        setLoading(true);

        const { data: response, error } = await tryCatch(genericService.post<FreshStartResponse>(nextStep));

        handleResponse(response, { setError }, error);
        setLoading(false);
    };

    return (
        <div className="flex flex-col w-full items-center gap-6">
            <CredentialsHeader
                title={t("phonenumber_login_fresh_start_release.header_title")}
                showLoading={loading}
                isTextAlignCenter={false}
                previousStepBackXTimes={backXTimes}
                showBackButton={true}
            />

            <div
                role="note"
                className="w-full flex gap-3 rounded-lg p-4 border border-[var(--outline-semantic-error)] bg-[var(--surface-semantic-error-variant)]"
            >
                <img src={alertCircle} alt="" className="h-8 w-8 shrink-0" />
                <p className="text-sm text-[var(--on-semantic-error-variant)]">
                    <b>{t("phonenumber_login_fresh_start_release.irreversible_warning_highlight")}</b>{" "}
                    {t("phonenumber_login_fresh_start_release.irreversible_warning_message")}
                </p>
            </div>

            <ul className="w-full flex flex-col gap-4 list-disc pl-6 marker:text-[var(--redContinente)]">
                {CONSEQUENCE_KEYS.map((key) => (
                    <li
                        key={key}
                        className="text-sm text-[var(--on-surface-default)]"
                        dangerouslySetInnerHTML={{ __html: t(key) }}
                    />
                ))}
            </ul>

            <div className="w-full">
                <Checkbox
                    ariaLabel={t("phonenumber_login_fresh_start_release.confirm_checkbox")}
                    onClick={() => setUnderstood((prev) => !prev)}
                    disabled={loading}
                    label={
                        <span className="text-sm">
                            {t("phonenumber_login_fresh_start_release.confirm_checkbox")}
                        </span>
                    }
                />
            </div>

            {errors.submitError?.message && (
                <SimpleErrorMessage
                    id="fresh_start_release__error-message"
                    error={errors.submitError.message}
                />
            )}

            <div className="h-full w-full flex flex-col gap-4 justify-center items-center">
                <Button
                    type="button"
                    disabled={loading || !understood}
                    onClick={release}
                    aria-label={t("accessibility.phonenumber.login.fresh_start_release.confirm")}
                >
                    {t("phonenumber_login_fresh_start_release.confirm_button")}
                </Button>
                <Button
                    type="button"
                    variant={"link"}
                    size={"link"}
                    className="h-auto no-underline font-medium text-[var(--on-surface-default)]"
                    disabled={loading}
                    onClick={cancel}
                    aria-label={t("accessibility.phonenumber.login.fresh_start_release.cancel")}
                >
                    {t("phonenumber_login_fresh_start_release.cancel_button")}
                </Button>
            </div>
        </div>
    );
}
